import React from "react";

import _ from "lodash";
import { compose, graphql } from "lib/apolloClient";
import { connect } from "react-redux";
import classNames from "classnames";
import { useI18n } from "Hooks";

import SkeletonLoader from "UIComponents/SkeletonLoader";

import { getMessagingV2ReduxState } from "MessagingV2/modules/MessagingModule";
import {
  CHAT_TYPES,
  MESSAGING_FILTERS_CONSTANTS,
} from "MessagingV2/modules/Utils";
import classes from "./ChatSuggestionsThread.scss";
import { getOrganizationId, getUserType } from "modules/CommonSelectors";
import { getChatSuggestionsQuery } from "../Utils/ThreadListQueries";
import { STAFF } from "Constants/stringConstants";
import AvailableChatSuggestion from "./AvailableChatSuggestion";
import DynamicChatSuggestions from "./DynamicChatSuggestion";
import { withRouter } from "react-router";

const getSequencedSuggestions = ({
  availableChats,
  dynamicFamilyChannelStudent,
  dynamicPersonalChatUser,
  selectedUserId,
  page,
}) => {
  switch (page) {
    case "STUDENT_PROFILE_PAGE":
    case "FAMILY_PROFILE_PAGE": {
      const suggestions = [];
      const tempAvailableChats = [...availableChats];
      const tempDynamicPersonalChatUser = [...dynamicPersonalChatUser];

      const selectedUserExistingChat = _.remove(tempAvailableChats, chat =>
        _.isEqual(_.get(chat, "personalChatReceiverUser.id"), selectedUserId)
      );

      if (!_.isEmpty(selectedUserExistingChat)) {
        suggestions.push({
          id: "1",
          isAvailableChat: true,
          chats: selectedUserExistingChat,
        });
      } else {
        const selectedUserDynamicChat = _.remove(
          tempDynamicPersonalChatUser,
          user => user.id === selectedUserId
        );
        if (!_.isEmpty(selectedUserDynamicChat)) {
          suggestions.push({
            id: "2",
            isAvailableChat: false,
            users: selectedUserDynamicChat,
          });
        }
      }

      const channels = _.remove(
        tempAvailableChats,
        chat => !_.isEqual(_.get(chat, "type"), CHAT_TYPES.CHAT_PERSONAL)
      );

      suggestions.push({
        id: "3",
        isAvailableChat: true,
        chats: channels,
      });

      suggestions.push({
        id: "4",
        isAvailableChat: false,
        isPersonalChat: false,
        users: dynamicFamilyChannelStudent,
      });

      suggestions.push({
        id: "5",
        isAvailableChat: true,
        chats: tempAvailableChats,
      });

      suggestions.push({
        id: "6",
        isAvailableChat: false,
        users: tempDynamicPersonalChatUser,
      });

      return suggestions;
    }
    default:
      return [
        {
          id: "1",
          isAvailableChat: true,
          chats: availableChats,
        },
        {
          id: "2",
          isAvailableChat: false,
          isPersonalChat: false,
          users: dynamicFamilyChannelStudent,
        },
        {
          id: "3",
          isAvailableChat: false,
          isPersonalChat: true,
          users: dynamicPersonalChatUser,
        },
      ];
  }
};

const ThreadLoader = () => {
  return (
    <div className={classes.threadLoaderContainer}>
      <SkeletonLoader variant="rounded" width="32px" height="32px" />
      <div className={classes.threadLoaderContentArea}>
        <div className={classes.threadLoaderHeader}>
          <SkeletonLoader variant="rounded" width="40%" height="20px" />
          <SkeletonLoader variant="rounded" width="20%" height="20px" />
        </div>
        <SkeletonLoader variant="rounded" width="20%" height="16px" />
      </div>
    </div>
  );
};

const ChatSuggestionsThreadWithData = props => {
  const { suggestions, onThreadClick, page, selectedUserId } = props;

  const availableChats = _.get(suggestions, "chats", []);
  const dynamicFamilyChannelStudent = _.get(
    suggestions,
    "studentsToCreateFamilyChannel",
    []
  );
  const dynamicPersonalChatUser = _.get(
    suggestions,
    "usersToCreatePersonalChat",
    []
  );

  const suggestionsSequences = getSequencedSuggestions({
    availableChats,
    dynamicFamilyChannelStudent,
    dynamicPersonalChatUser,
    page,
    selectedUserId,
  });

  return _.map(suggestionsSequences, suggestionsSequence => {
    if (_.get(suggestionsSequence, "isAvailableChat")) {
      return (
        <AvailableChatSuggestion
          key={_.get(suggestionsSequence, "id")}
          chats={_.get(suggestionsSequence, "chats", [])}
          onThreadClick={onThreadClick}
        />
      );
    }

    return (
      <DynamicChatSuggestions
        key={_.get(suggestionsSequence, "id")}
        users={_.get(suggestionsSequence, "users", [])}
        isPersonalChat={_.get(suggestionsSequence, "isPersonalChat", true)}
      />
    );
  });
};

const isSuggestionsAvailable = ({ suggestions, isData, isLoading }) => {
  if (!isData && isLoading) {
    return true;
  }

  const chats = _.get(suggestions, "chats", []);
  const studentsToCreateFamilyChannel = _.get(
    suggestions,
    "studentsToCreateFamilyChannel",
    []
  );

  const usersToCreatePersonalChat = _.get(
    suggestions,
    "usersToCreatePersonalChat",
    []
  );

  if (
    _.isEmpty(chats) &&
    _.isEmpty(studentsToCreateFamilyChannel) &&
    _.isEmpty(usersToCreatePersonalChat)
  ) {
    return false;
  }

  return true;
};

const ChatSuggestionsThread = props => {
  const {
    suggestions = {},
    isData,
    isLoading,
    onThreadClick,
    showChatSuggestions,
    page,
    selectedUserId,
  } = props;
  const { t } = useI18n();

  if (
    !showChatSuggestions ||
    !isSuggestionsAvailable({ suggestions, isData, isLoading })
  ) {
    return null;
  }

  const showLoader = isLoading && !isData;

  return (
    <div className={classes.chatSuggestionsThreadContainer}>
      <div className={classNames(classes.filterTitle, "text-body")}>
        {t("common:suggestions")}
      </div>
      {showLoader ? (
        <ThreadLoader />
      ) : (
        <ChatSuggestionsThreadWithData
          suggestions={suggestions}
          onThreadClick={onThreadClick}
          page={page}
          selectedUserId={selectedUserId}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const studentId = _.get(ownProps, "router.params.student_id");
  const familyId = _.get(ownProps, "router.params.familyId");
  const teacherId = _.get(ownProps, "router.params.teacherId");

  const selectedCourseId = _.get(
    state,
    "teacher.selected_class.selected_course"
  );

  const messagingState = getMessagingV2ReduxState(state);

  const { threadFilter } = _.get(
    messagingState,
    "messageRevampV2FilterState",
    {}
  );

  const showChatSuggestions =
    _.includes([MESSAGING_FILTERS_CONSTANTS.ALL], threadFilter) &&
    _.isEqual(getUserType(), STAFF);

  return {
    showChatSuggestions,
    selectedCourseId,
    studentId,
    familyId,
    teacherId,
  };
};

const getSuggestionInput = ({
  selectedCourseId,
  studentId,
  familyId,
  teacherId,
}) => {
  if (studentId) {
    return {
      userId: studentId,
      page: "STUDENT_PROFILE_PAGE",
    };
  }
  if (familyId) {
    return {
      userId: familyId,
      page: "FAMILY_PROFILE_PAGE",
    };
  }

  if (teacherId) {
    return {
      userId: teacherId,
      page: "STAFF_PROFILE_PAGE",
    };
  }

  if (selectedCourseId) {
    return {
      courseId: selectedCourseId,
      page: "COURSE_PAGE",
    };
  }

  return {
    page: "HOME_PAGE",
  };
};

export default compose(
  withRouter,
  connect(mapStateToProps),
  graphql(getChatSuggestionsQuery, {
    name: "chatSuggestions",
    skip: ({ showChatSuggestions }) => !showChatSuggestions,
    options: ({ selectedCourseId, studentId, familyId, teacherId }) => ({
      variables: {
        organizationId: getOrganizationId(),
        portalType: "PLANNER",
        input: {
          ...getSuggestionInput({
            studentId,
            familyId,
            teacherId,
            selectedCourseId,
          }),
        },
      },
    }),
    props: ({ chatSuggestions }) => {
      const { node = {} } = chatSuggestions;
      const suggestions = _.get(node, "chatSuggestions", {});
      const variables = _.get(chatSuggestions, "variables", {});
      const page = _.get(variables, "input.page", "");
      return {
        suggestions,
        selectedUserId: _.get(variables, "input.userId"),
        page,
        isData: !_.isEmpty(node),
        isLoading: _.includes([1, 2], chatSuggestions["networkStatus"]),
      };
    },
  })
)(ChatSuggestionsThread);
